import { dashboardApi } from '../dashboard-api';
import { CustomEventChart } from '../insight-cards/types';
import {
  ABTestAIRecommendationResponse,
  CreateOrUpdateSolveABTestConfig,
  GenerateVariantSummaryRequest,
  GenerateVariantSummaryResponse,
  ListAvailableWinnerMetrics,
  ListSolveABTestConfigs,
  ListSolveABTestConfigsSummary,
  RenameSolveABTestConfig,
  SolveABTestConfig,
  SolveABTestVariant,
  ValidateAbTestConfigResponse,
  WinnerResponse,
  WorkflowABTestVariantsResponse,
} from './types';

const BASE_URL = 'dashboard-controls/solve-ab-tests/v1';

const agentAbTestApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    createAbTestConfig: builder.mutation<
      SolveABTestConfig,
      CreateOrUpdateSolveABTestConfig
    >({
      invalidatesTags: [{ id: 'LIST', type: 'AbTestConfigs' }],
      query: body => ({
        body,
        method: 'POST',
        url: `${BASE_URL}/configs`,
      }),
    }),
    deleteAbTestConfig: builder.mutation<void, string>({
      invalidatesTags: (_result, _error, id) => [
        { id, type: 'AbTestConfigs' },
        { id: 'LIST', type: 'AbTestConfigs' },
      ],
      query: abTestConfigId => ({
        method: 'DELETE',
        url: `${BASE_URL}/configs/${abTestConfigId}`,
      }),
    }),
    duplicateAbTestConfig: builder.mutation<SolveABTestConfig, string>({
      invalidatesTags: [{ id: 'LIST', type: 'AbTestConfigs' }],
      query: abTestConfigId => ({
        method: 'POST',
        url: `${BASE_URL}/configs/${abTestConfigId}/duplicate`,
      }),
    }),
    generateVariantSummaries: builder.query<
      GenerateVariantSummaryResponse[],
      {
        abTestConfigId: string;
        intentWorkflowId: string;
        variants: SolveABTestVariant[];
        variantsKey: string;
      }
    >({
      async queryFn(
        { abTestConfigId, intentWorkflowId, variants },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        const results = await Promise.allSettled(
          variants.map(variant =>
            fetchWithBQ({
              body: {
                intent_workflow_id: intentWorkflowId,
                intent_workflow_version: variant.intent_workflow_version,
                variant_name: variant.name ?? '',
                version_type: variant.version_type,
              },
              method: 'POST',
              url: `${BASE_URL}/configs/${abTestConfigId}/variant-summary`,
            }),
          ),
        );
        const summaries = results
          .filter(
            (
              r,
            ): r is PromiseFulfilledResult<{
              data: GenerateVariantSummaryResponse;
            }> => r.status === 'fulfilled' && 'data' in r.value,
          )
          .map(r => r.value.data);
        return { data: summaries };
      },
    }),
    generateVariantSummary: builder.mutation<
      GenerateVariantSummaryResponse,
      { abTestConfigId: string; body: GenerateVariantSummaryRequest }
    >({
      query: ({ abTestConfigId, body }) => ({
        body,
        method: 'POST',
        url: `${BASE_URL}/configs/${abTestConfigId}/variant-summary`,
      }),
    }),
    getAbTestAIRecommendation: builder.query<
      ABTestAIRecommendationResponse,
      string
    >({
      providesTags: (_result, _error, id) => [
        { id, type: 'AbTestAIRecommendation' },
      ],
      query: abTestConfigId => ({
        method: 'GET',
        url: `${BASE_URL}/configs/${abTestConfigId}/ai-recommendation`,
      }),
    }),
    getAbTestConfigById: builder.query<SolveABTestConfig, string>({
      providesTags: (_result, _error, id) => [{ id, type: 'AbTestConfigs' }],
      query: abTestConfigId => ({
        method: 'GET',
        url: `${BASE_URL}/configs/${abTestConfigId}`,
      }),
    }),
    getAbTestConversationCharts: builder.query<CustomEventChart[], string>({
      query: abTestConfigId => ({
        method: 'GET',
        url: `${BASE_URL}/configs/${abTestConfigId}/conversations/charts`,
      }),
    }),
    getAbTestResultCharts: builder.query<CustomEventChart[], string>({
      providesTags: (_result, _error, id) => [
        { id, type: 'AbTestResultsCharts' },
      ],
      query: abTestConfigId => ({
        method: 'GET',
        url: `${BASE_URL}/configs/${abTestConfigId}/results/charts`,
      }),
    }),
    getAbTestSummaryCharts: builder.query<CustomEventChart[], void>({
      query: () => ({
        method: 'GET',
        url: `${BASE_URL}/configs/summary/charts`,
      }),
    }),
    getWinner: builder.query<WinnerResponse, string>({
      providesTags: (_result, _error, id) => [
        { id, type: 'AbTestConfigsWinner' },
      ],
      query: abTestConfigId => ({
        method: 'GET',
        url: `${BASE_URL}/configs/${abTestConfigId}/winner`,
      }),
    }),
    getWorkflowAbTestVariants: builder.query<
      WorkflowABTestVariantsResponse,
      { end?: number; start?: number } | void
    >({
      providesTags: [{ id: 'LIST', type: 'AbTestConfigs' }],
      query: params => ({
        method: 'GET',
        params: params || undefined,
        url: `${BASE_URL}/configs/workflow-variants`,
      }),
    }),
    listAbTestConfigs: builder.query<
      ListSolveABTestConfigs,
      { page?: number; pageSize?: number }
    >({
      providesTags: result =>
        result
          ? [
              ...result.ab_test_configs.map(
                ({ ab_test_config_id }) =>
                  ({
                    id: ab_test_config_id,
                    type: 'AbTestConfigs',
                  }) as const,
              ),
              { id: 'LIST', type: 'AbTestConfigs' },
            ]
          : [{ id: 'LIST', type: 'AbTestConfigs' }],
      query: ({ page = 1, pageSize = 100 }) => ({
        method: 'GET',
        params: { page, page_size: pageSize },
        url: `${BASE_URL}/configs`,
      }),
    }),
    listAbTestConfigsSummary: builder.query<
      ListSolveABTestConfigsSummary,
      { page?: number; pageSize?: number }
    >({
      providesTags: result =>
        result
          ? [
              ...result.ab_test_configs.map(
                ({ ab_test_config_id }) =>
                  ({
                    id: ab_test_config_id,
                    type: 'AbTestConfigs',
                  }) as const,
              ),
              { id: 'LIST', type: 'AbTestConfigs' },
            ]
          : [{ id: 'LIST', type: 'AbTestConfigs' }],
      query: ({ page = 1, pageSize = 100 }) => ({
        method: 'GET',
        params: { page, page_size: pageSize },
        url: `${BASE_URL}/configs/summary`,
      }),
    }),
    listAvailableWinnerMetrics: builder.query<ListAvailableWinnerMetrics, void>(
      {
        query: () => ({
          method: 'GET',
          url: `${BASE_URL}/configs/winner-metrics`,
        }),
      },
    ),
    regenerateAbTestAIRecommendation: builder.mutation<
      ABTestAIRecommendationResponse,
      string
    >({
      invalidatesTags: (_result, _error, id) => [
        { id, type: 'AbTestAIRecommendation' },
      ],
      query: abTestConfigId => ({
        method: 'POST',
        url: `${BASE_URL}/configs/${abTestConfigId}/ai-recommendation/regenerate`,
      }),
    }),
    renameAbTestConfig: builder.mutation<
      SolveABTestConfig,
      { abTestConfigId: string; body: RenameSolveABTestConfig }
    >({
      invalidatesTags: (_result, _error, { abTestConfigId }) => [
        { id: abTestConfigId, type: 'AbTestConfigs' },
        { id: 'LIST', type: 'AbTestConfigs' },
      ],
      query: ({ abTestConfigId, body }) => ({
        body,
        method: 'POST',
        url: `${BASE_URL}/configs/${abTestConfigId}/rename`,
      }),
    }),
    startAbTestConfig: builder.mutation<SolveABTestConfig, string>({
      invalidatesTags: (_result, _error, id) => [
        { id, type: 'AbTestConfigs' },
        { id: 'LIST', type: 'AbTestConfigs' },
        { id, type: 'AbTestResultsCharts' },
        'AutoflowVersionHistorySummary',
      ],
      query: abTestConfigId => ({
        method: 'POST',
        url: `${BASE_URL}/configs/${abTestConfigId}/start`,
      }),
    }),
    stopAbTestConfig: builder.mutation<SolveABTestConfig, string>({
      invalidatesTags: (_result, _error, id) => [
        { id, type: 'AbTestConfigs' },
        { id: 'LIST', type: 'AbTestConfigs' },
        { id, type: 'AbTestResultsCharts' },
        { id, type: 'AbTestConfigsWinner' },
        'AutoflowVersionHistorySummary',
      ],
      query: abTestConfigId => ({
        method: 'POST',
        url: `${BASE_URL}/configs/${abTestConfigId}/stop`,
      }),
    }),
    updateAbTestConfig: builder.mutation<
      SolveABTestConfig,
      { abTestConfigId: string; body: CreateOrUpdateSolveABTestConfig }
    >({
      invalidatesTags: (_result, _error, { abTestConfigId }) =>
        _error ? [] : [{ id: abTestConfigId, type: 'AbTestConfigs' }],
      onQueryStarted: async (
        { abTestConfigId, body },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          agentAbTestApi.util.updateQueryData(
            'getAbTestConfigById',
            abTestConfigId,
            draft => {
              Object.assign(draft, body);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      query: ({ abTestConfigId, body }) => ({
        body,
        method: 'POST',
        url: `${BASE_URL}/configs/${abTestConfigId}`,
      }),
    }),
    validateAbTestConfig: builder.mutation<
      ValidateAbTestConfigResponse,
      string
    >({
      query: abTestConfigId => ({
        method: 'POST',
        url: `${BASE_URL}/configs/${abTestConfigId}/validate`,
      }),
    }),
  }),
});

export const {
  useCreateAbTestConfigMutation,
  useDeleteAbTestConfigMutation,
  useDuplicateAbTestConfigMutation,
  useGenerateVariantSummariesQuery,
  useGenerateVariantSummaryMutation,
  useGetAbTestAIRecommendationQuery,
  useGetAbTestConfigByIdQuery,
  useGetAbTestConversationChartsQuery,
  useGetAbTestResultChartsQuery,
  useGetAbTestSummaryChartsQuery,
  useGetWinnerQuery,
  useGetWorkflowAbTestVariantsQuery,
  useListAbTestConfigsQuery,
  useListAbTestConfigsSummaryQuery,
  useListAvailableWinnerMetricsQuery,
  useRegenerateAbTestAIRecommendationMutation,
  useRenameAbTestConfigMutation,
  useStartAbTestConfigMutation,
  useStopAbTestConfigMutation,
  useUpdateAbTestConfigMutation,
  useValidateAbTestConfigMutation,
} = agentAbTestApi;

export { agentAbTestApi };
